/* eslint-disable no-unused-vars */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GlowingOrb from "../components/ui/GlowingOrb";
import DOMAIN_CONFIG from "../config/domains";

const TypeProtectedRoute = ({ children, userType }) => {
  const {
    user,
    userType: currentUserType,
    isSignedUp,
    hasActiveSubscription,
    subscriptionStatus,
    loading,
    initialAuthCheckDone,
    crossDomainAuthPending,
  } = useAuth();
  const location = useLocation();

  // Check localStorage as fallback for race condition during login
  const localStorageUserType = localStorage.getItem("userType");
  const localStorageIsSignedUp = localStorage.getItem("isSignedUp");

  // Use context values, but fall back to localStorage if context hasn't updated yet
  const effectiveUserType = currentUserType || localStorageUserType;
  const effectiveIsSignedUp = isSignedUp || localStorageIsSignedUp === "true";

  // Wait for Firebase to fully restore auth state AND cross-domain auth to complete
  if (loading || !initialAuthCheckDone || crossDomainAuthPending) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <GlowingOrb size="md" />
      </div>
    );
  }

  if (!user) {
    // Use cross-domain redirect to auth domain
    DOMAIN_CONFIG.redirectToAuth("/login");
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <GlowingOrb size="md" />
      </div>
    );
  }

  if (
    user &&
    !effectiveIsSignedUp &&
    location.pathname === "/stripe-callback"
  ) {
    return children;
  }

  // Allow MPS signup form for MPS users
  if (
    user &&
    !effectiveIsSignedUp &&
    userType === "MPS" &&
    location.pathname === "/mps-signup"
  ) {
    return children;
  }

  const subscriptionAllowedPaths = [
    "/eo-paywall",
    "/subscription-success",
    "/subscription-cancel",
    "/eo/complete-profile",
    "/stripe-connect-callback",
  ];

  if (
    user &&
    userType === "EO" &&
    (effectiveUserType === "EO" || effectiveUserType === null)
  ) {
    if (
      subscriptionAllowedPaths.some((path) =>
        location.pathname.startsWith(path),
      )
    ) {
      return children;
    }
  }

  // Redirect signed up users away from signup pages
  if (user && effectiveIsSignedUp && location.pathname === "/mps-signup") {
    return <Navigate to="/mps/dashboard" replace />;
  }

  if (user && !effectiveIsSignedUp && userType === "MPS") {
    return <Navigate to="/mps-signup" />;
  }

  // Only redirect if we have a confirmed userType mismatch
  // Don't redirect if effectiveUserType is null (still loading from Firestore)
  if (effectiveUserType && effectiveUserType !== userType) {
    return (
      <Navigate to={`/${effectiveUserType.toLowerCase()}/dashboard`} replace />
    );
  }

  // If effectiveUserType is null but user exists, wait for it to resolve
  if (!effectiveUserType && user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <GlowingOrb size="md" />
      </div>
    );
  }

  return children;
};

export default TypeProtectedRoute;
